import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Archive, ChevronDown, ChevronRight, Loader2, RotateCcw } from "lucide-react";

// Services Archive panel — non-destructive snapshots of pre-migration
// service configurations kept per the Phase Services IA Restructure
// Legacy-data safety spec. Each row shows the archived slug + reason +
// timestamp and can be restored back into the live `services`
// collection with one click.
export const ServicesArchivePanel = () => {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/services-archive");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load archive");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const restore = async (row) => {
    const slug = row.slug;
    if (!window.confirm(
      `Restore "${row.name}" (slug=${slug}) from the archive?\n\n` +
      `The current live service at that slug will be snapshotted into the ` +
      `archive first — no data will be lost.`
    )) return;
    setRestoringId(row.id);
    try {
      await api.post(`/admin/services-archive/${row.id}/restore`);
      toast.success(`Restored ${row.name} — refresh the Services tab to see it live.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-[#A3A3A3] py-8 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading archive…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="ur-card p-10 text-center text-sm text-[#A3A3A3]">
        <Archive className="w-6 h-6 mx-auto mb-3 text-[#7A7A7A]" />
        No archived service snapshots yet.
      </div>
    );
  }

  return (
    <div data-testid="services-archive-panel" className="space-y-3">
      <div className="text-xs text-[#A3A3A3] leading-relaxed">
        Pre-migration snapshots of legacy service configurations. Restoring a snapshot
        overwrites the live service at the same slug with the archived data
        (and snapshots the current live version back into the archive first —
        restores are non-destructive too).
      </div>
      {rows.map((r) => {
        const open = expanded === r.id;
        const snap = r.snapshot || {};
        return (
          <div
            key={r.id}
            data-testid={`archive-row-${r.id}`}
            className="ur-card p-5"
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setExpanded(open ? null : r.id)}
                className="flex items-center gap-3 text-left flex-1 min-w-0"
              >
                {open ? (
                  <ChevronDown className="w-4 h-4 text-[#FF6600]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <div className="font-display font-bold">{r.name}</div>
                    <div className="text-xs text-[#A3A3A3]">slug={r.slug}</div>
                  </div>
                  <div className="text-[11px] text-[#7A7A7A] mt-1">
                    {r.reason} · {new Date(r.archived_at).toLocaleString()} · by {r.archived_by || "system"}
                  </div>
                </div>
              </button>
              <button
                data-testid={`archive-restore-${r.id}`}
                onClick={() => restore(r)}
                disabled={restoringId === r.id}
                className="ur-btn-secondary text-xs py-2 px-3.5 disabled:opacity-40"
              >
                {restoringId === r.id ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Restoring…
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </>
                )}
              </button>
            </div>
            {open && (
              <div className="mt-4 pt-4 border-t border-[#262626] grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Snapshot Summary</div>
                  <div className="mt-2 space-y-1">
                    <Row label="Name" value={snap.name} />
                    <Row label="Pricing Model" value={snap.pricing_model} />
                    <Row label="CTA Type" value={snap.cta_type} />
                    <Row label="Tiers" value={(snap.tiers || []).length} />
                    <Row label="Add-ons" value={(snap.addons || []).length} />
                    <Row label="Items" value={(snap.standalone_items || []).length} />
                    <Row label="Kits" value={(snap.kits || []).length} />
                    <Row label="Active" value={String(snap.active)} />
                    <Row label="Featured" value={String(snap.featured)} />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Raw JSON</div>
                  <pre className="mt-2 text-[11px] text-[#A3A3A3] bg-[#0A0A0A] border border-[#262626] rounded-lg p-3 max-h-[400px] overflow-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(snap, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-2 text-xs">
    <span className="text-[#7A7A7A] w-28 shrink-0">{label}</span>
    <span className="text-[#D1D1D1] break-all">
      {value === "" || value === undefined || value === null ? "—" : String(value)}
    </span>
  </div>
);
